import { useEffect, useRef } from 'react';
import { navigate, usePathname } from 'wouter/use-browser-location';
import { SHEET_HISTORY_STATE } from '../constants.ts';
import type { BrowserLocationHook } from 'wouter/use-browser-location';

// On a phone the back button is how a panel is dismissed. Each open sheet
// pushes one history entry (same URL, marked in its state) so that Back pops
// the entry and the sheet closes instead of the page changing. A sheet that
// closes any other way pops its own entry, so Back afterwards leaves the
// page as it always did.
//
// history.back() is asynchronous: a navigation started before its popstate
// lands (a tap right after a form closes) would be undone by the traversal.
// So the app routes every navigation through `useSheetAwareLocation`, which
// holds one until the pops it is waiting on have landed, and which pops the
// open sheets' entries first when a page navigates away from under them.

/** Reports whether the sheet is closing in answer to Back. */
type BackListener = () => boolean;

interface OpenSheet {
  readonly id: symbol;
  readonly onBack: BackListener;
}

/** Open sheets in stacking order; the last is what Back should close. */
const open: OpenSheet[] = [];
/** Sheet entries on the history stack that nothing has popped yet. */
let entries = 0;
/** Our own history.go() calls whose popstate has not arrived. */
let pendingPops = 0;
/** Navigations waiting for those pops. */
let deferred: (() => void)[] = [];

function isSheetEntry(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { readonly sheet?: unknown }).sheet === SHEET_HISTORY_STATE.sheet
  );
}

function pushEntry(): void {
  history.pushState(SHEET_HISTORY_STATE, '', window.location.href);
  entries += 1;
}

function popEntries(count: number): void {
  entries -= count;
  pendingPops += 1;
  history.go(-count);
}

function onPopState(): void {
  if (pendingPops > 0) {
    pendingPops -= 1;
    if (pendingPops === 0) {
      const waiting = deferred;
      deferred = [];
      for (const run of waiting) {
        run();
      }
    }
    return;
  }
  const top = open.at(-1);
  if (top === undefined || entries === 0) {
    return;
  }
  entries -= 1;
  if (!top.onBack()) {
    // The sheet stays open (it is asking about unsaved changes), so Back
    // must keep working: put its entry back.
    pushEntry();
  }
}

let listening = false;
function listen(): void {
  if (!listening) {
    listening = true;
    window.addEventListener('popstate', onPopState);
  }
}

/**
 * Gives a mounted sheet its history entry. `onBack` runs when Back pops
 * that entry and reports whether the sheet is closing; a sheet that stays
 * open gets its entry back.
 *
 * The pop on unmount waits a microtask: React's StrictMode unmounts and
 * remounts an effect at once in development, and a pop already issued
 * cannot be taken back, so a remount cancels it and keeps the entry.
 */
export function useSheetHistory(onBack: BackListener): void {
  const latest = useRef(onBack);
  useEffect(() => {
    latest.current = onBack;
  }, [onBack]);
  const pendingPop = useRef<{ cancelled: boolean } | null>(null);
  useEffect(() => {
    listen();
    const id = Symbol('sheet');
    open.push({ id, onBack: () => latest.current() });
    if (pendingPop.current === null) {
      pushEntry();
    } else {
      pendingPop.current.cancelled = true;
      pendingPop.current = null;
    }
    return () => {
      const at = open.findIndex((sheet) => sheet.id === id);
      if (at !== -1) {
        open.splice(at, 1);
      }
      const token = { cancelled: false };
      pendingPop.current = token;
      queueMicrotask(() => {
        if (token.cancelled) {
          return;
        }
        pendingPop.current = null;
        if (entries > 0 && isSheetEntry(history.state)) {
          popEntries(1);
        }
      });
    };
  }, []);
}

/** wouter's navigate, ordered after any sheet entry that is still on the stack. */
function navigateAfterSheets(...args: Parameters<typeof navigate>): void {
  if (entries > 0) {
    popEntries(entries);
  }
  if (pendingPops > 0) {
    deferred.push(() => {
      navigate(...args);
    });
    return;
  }
  navigate(...args);
}

/** The app's location hook: the browser's, with sheet-aware navigation. */
export const useSheetAwareLocation: BrowserLocationHook = (options) => [
  usePathname(options),
  navigateAfterSheets,
];
